import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, type Receipt, type Transfer, type Warehouse } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, EmptyState, Field, Input, Notice, Select, Spinner } from '../ui/controls'
import ProductPicker from '../ui/ProductPicker'

/**
 * Документы прихода (п. 4.10) и перемещения (п. 4.12).
 *
 * Документ создаётся черновиком и редактируется до проводки. Проводка
 * создаёт движения по всем строкам одной транзакцией; проведённый
 * документ доступен только для просмотра.
 */
export default function DocumentsPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.stock_ops

  const [tab, setTab] = useState<'receipts' | 'transfers'>('receipts')
  const [receipts, setReceipts] = useState<Receipt[] | null>(null)
  const [transfers, setTransfers] = useState<Transfer[] | null>(null)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [open, setOpen] = useState<{ kind: 'receipt' | 'transfer'; id: number } | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)

  const reload = useCallback(async () => {
    const [r, t] = await Promise.all([api.receipts(), api.transfers()])
    setReceipts(r)
    setTransfers(t)
  }, [])

  useEffect(() => {
    void reload()
    void api.warehouses().then((w) => setWarehouses(w.filter((x) => x.is_active)))
  }, [reload])

  if (open) {
    return (
      <DocumentSheet
        kind={open.kind}
        id={open.id}
        canWrite={canWrite}
        onClose={async () => {
          setOpen(null)
          await reload()
        }}
      />
    )
  }

  if (receipts === null || transfers === null) return <Spinner label="Загрузка документов…" />

  const list = tab === 'receipts' ? receipts : transfers

  return (
    <section className="max-w-4xl">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Документы склада</h2>
        <p className="mt-0.5 text-sm text-ink-500">
          Проведённый документ не редактируется — исправление выполняется корректировкой
          либо обратным документом.
        </p>
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      <div className="mb-4 flex gap-1 border-b border-ink-200 dark:border-ink-800">
        {(
          [
            ['receipts', `Приход (${receipts.length})`],
            ['transfers', `Перемещение (${transfers.length})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-brand-600 text-brand-700 dark:text-brand-300'
                : 'border-transparent text-ink-500 hover:text-ink-700 dark:hover:text-ink-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {canWrite && warehouses.length > 0 && (
        <CreateBar
          kind={tab}
          warehouses={warehouses}
          onCreated={async (kind, id) => {
            await reload()
            setOpen({ kind, id })
          }}
          onError={(text) => setNotice({ kind: 'error', text })}
        />
      )}

      {list.length === 0 ? (
        <EmptyState
          title="Документов нет"
          hint={
            tab === 'receipts'
              ? 'Создайте документ прихода, чтобы оприходовать товар на склад.'
              : 'Создайте документ перемещения, чтобы передать товар между складами.'
          }
        />
      ) : (
        <ul className="divide-y divide-ink-200 rounded-lg border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {tab === 'receipts'
            ? receipts.map((d) => (
                <DocRow
                  key={d.id}
                  number={d.number}
                  info={`${d.warehouse_name} · строк ${d.line_count}${d.supplier ? ` · ${d.supplier}` : ''}`}
                  date={d.created_at}
                  state={d.state}
                  stateTitle={d.state_title}
                  onOpen={() => setOpen({ kind: 'receipt', id: d.id })}
                />
              ))
            : transfers.map((d) => (
                <DocRow
                  key={d.id}
                  number={d.number}
                  info={`${d.source_warehouse_name} → ${d.target_warehouse_name} · строк ${d.line_count}`}
                  date={d.created_at}
                  state={d.state}
                  stateTitle={d.state_title}
                  onOpen={() => setOpen({ kind: 'transfer', id: d.id })}
                />
              ))}
        </ul>
      )}
    </section>
  )
}

function DocRow({
  number,
  info,
  date,
  state,
  stateTitle,
  onOpen,
}: {
  number: string
  info: string
  date: string
  state: string
  stateTitle: string
  onOpen: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-ink-50 dark:hover:bg-ink-900"
      >
        <span className="font-mono text-sm text-ink-700 dark:text-ink-300">{number}</span>
        <span className="min-w-0 flex-1 truncate text-sm text-ink-600 dark:text-ink-400">
          {info}
        </span>
        <span className="text-xs text-ink-400">{date}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            state === 'posted'
              ? 'bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100'
              : 'bg-warning-100 dark:bg-warning-700/25 text-warning-700 dark:text-warning-100'
          }`}
        >
          {stateTitle}
        </span>
      </button>
    </li>
  )
}

function CreateBar({
  kind,
  warehouses,
  onCreated,
  onError,
}: {
  kind: 'receipts' | 'transfers'
  warehouses: Warehouse[]
  onCreated: (kind: 'receipt' | 'transfer', id: number) => Promise<void>
  onError: (text: string) => void
}) {
  const [source, setSource] = useState(warehouses[0]?.id ?? 0)
  const [target, setTarget] = useState(warehouses[1]?.id ?? warehouses[0]?.id ?? 0)
  const [supplier, setSupplier] = useState('')
  const [pending, setPending] = useState(false)

  return (
    <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-ink-200 p-3 dark:border-ink-800">
      <LabeledSelect
        label={kind === 'receipts' ? 'Склад' : 'Откуда'}
        value={source}
        onChange={setSource}
        options={warehouses}
      />
      {kind === 'transfers' && (
        <LabeledSelect label="Куда" value={target} onChange={setTarget} options={warehouses} />
      )}
      {kind === 'receipts' && (
        <div className="w-56">
          <Field label="Поставщик">
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </Field>
        </div>
      )}
      <Button
        variant="primary"
        size="sm"
        disabled={pending || (kind === 'transfers' && source === target)}
        onClick={async () => {
          setPending(true)
          try {
            if (kind === 'receipts') {
              const d = await api.createReceipt({
                warehouse_id: source,
                supplier: supplier.trim() || null,
              })
              await onCreated('receipt', d.id)
            } else {
              const d = await api.createTransfer({
                source_warehouse_id: source,
                target_warehouse_id: target,
              })
              await onCreated('transfer', d.id)
            }
          } catch (e) {
            onError(e instanceof ApiError ? e.message : 'Не удалось создать документ')
          } finally {
            setPending(false)
          }
        }}
      >
        Создать документ
      </Button>
      {kind === 'transfers' && source === target && (
        <span className="py-2 text-xs text-warning-700 dark:text-warning-100">Склады должны различаться</span>
      )}
    </div>
  )
}

/**
 * Список выбора с подписью — обёртка над общим Select.
 *
 * Метрики берутся из общего органа управления, здесь только подпись
 * и приведение значения.
 */
function LabeledSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: number
  options: Warehouse[]
  onChange: (v: number) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-500">{label}</span>
      <Select value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {options.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </Select>
    </label>
  )
}

/** Карточка документа со строками и проводкой. */
function DocumentSheet({
  kind,
  id,
  canWrite,
  onClose,
}: {
  kind: 'receipt' | 'transfer'
  id: number
  canWrite: boolean
  onClose: () => Promise<void>
}) {
  const [doc, setDoc] = useState<Receipt | Transfer | null>(null)
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [confirmPost, setConfirmPost] = useState(false)
  const [pending, setPending] = useState(false)

  const load = useCallback(async () => {
    setDoc(kind === 'receipt' ? await api.receipt(id) : await api.transfer(id))
  }, [kind, id])

  useEffect(() => {
    void load()
  }, [load])

  if (doc === null) return <Spinner label="Загрузка документа…" />

  const editable = doc.editable && canWrite
  const sourceWarehouse = kind === 'transfer' ? (doc as Transfer).source_warehouse_id : undefined

  async function setLine(productId: number, quantity: number, accumulate = false) {
    try {
      if (kind === 'receipt') {
        setDoc(await api.setReceiptLine(id, { product_id: productId, quantity, accumulate }))
      } else {
        setDoc(await api.setTransferLine(id, { product_id: productId, quantity }))
      }
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось изменить' })
    }
  }

  return (
    <section className="max-w-3xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onClose}
            className="mb-1 text-xs text-ink-500 hover:text-brand-600"
          >
            ← К перечню документов
          </button>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">
            {kind === 'receipt' ? 'Приход' : 'Перемещение'} {doc.number}
          </h2>
          <p className="mt-0.5 text-sm text-ink-500">
            {kind === 'receipt'
              ? (doc as Receipt).warehouse_name
              : `${(doc as Transfer).source_warehouse_name} → ${(doc as Transfer).target_warehouse_name}`}
            {' · '}
            {doc.state_title}
            {doc.posted_at && ` ${doc.posted_at}`}
          </p>
        </div>

        {editable && (
          <Button
            variant="primary"
            disabled={pending || (doc.lines?.length ?? 0) === 0}
            onClick={() => setConfirmPost(true)}
          >
            Провести
          </Button>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {editable && (
        <div className="mb-4 max-w-lg">
          <span className="mb-1.5 block text-sm font-medium text-ink-700 dark:text-ink-300">
            Добавить позицию
          </span>
          <ProductPicker
            warehouseId={sourceWarehouse}
            placeholder="Артикул, штрихкод или название — Enter добавляет"
            onPick={(item) => void setLine(item.product_id, 1, kind === 'receipt')}
          />
          <p className="mt-1 text-xs text-ink-500">
            Повторный ввод того же кода увеличивает количество на единицу.
          </p>
        </div>
      )}

      {(doc.lines?.length ?? 0) === 0 ? (
        <EmptyState title="В документе нет строк" hint="Добавьте позиции — без них провести документ нельзя." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
              <tr>
                <th className="px-3 py-2.5 font-medium">Товар</th>
                <th className="px-3 py-2.5 text-right font-medium">Количество</th>
                {editable && <th className="w-10 px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
              {(doc.lines ?? []).map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2">
                    <span className="text-ink-800 dark:text-ink-200">{l.product_name}</span>
                    <span className="ml-2 font-mono text-xs text-ink-400">{l.product_sku}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editable ? (
                      <input
                        type="number"
                        min={1}
                        defaultValue={l.quantity}
                        onBlur={(e) => {
                          const v = Number(e.target.value)
                          if (v !== l.quantity) void setLine(l.product_id, v)
                        }}
                        className="w-24 rounded border border-ink-300 px-2 py-1 text-right text-sm tabular-nums dark:border-ink-700 dark:bg-ink-900"
                      />
                    ) : (
                      <span className="tabular-nums">{l.quantity}</span>
                    )}
                  </td>
                  {editable && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        aria-label="Удалить строку"
                        onClick={() => void setLine(l.product_id, 0)}
                        className="text-xs text-ink-400 hover:text-danger-700"
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmPost && (
        <ConfirmDialog
          title="Провести документ?"
          message={
            <>
              Будут созданы движения по всем {doc.lines?.length} строкам одной операцией.
              После проводки документ станет доступен только для просмотра.
              {kind === 'transfer' && (
                <>
                  <br />
                  <br />
                  Если на складе-источнике не хватит остатка, проводка будет отклонена
                  целиком.
                </>
              )}
            </>
          }
          confirmLabel="Провести"
          pending={pending}
          onCancel={() => setConfirmPost(false)}
          onConfirm={async () => {
            setPending(true)
            try {
              if (kind === 'receipt') await api.postReceipt(id)
              else await api.postTransfer(id)
              setConfirmPost(false)
              setNotice({ kind: 'success', text: 'Проведено' })
              await load()
            } catch (e) {
              setConfirmPost(false)
              if (e instanceof ApiError) {
                const d = e.details as { available?: number }
                setNotice({
                  kind: 'error',
                  text:
                    d.available !== undefined
                      ? `${e.message}. Доступно: ${d.available}.`
                      : e.message,
                })
              } else {
                setNotice({ kind: 'error', text: 'Не удалось провести' })
              }
            } finally {
              setPending(false)
            }
          }}
        />
      )}
    </section>
  )
}
